import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, Clock3, Loader2, MapPin, Search, Ticket, X } from "lucide-react";

const PINCODE_API_BASE = "https://pincode.indraq.info/api/v1";

const CONDITION_TYPES = [
  {
    value: "NONE",
    label: "Normal coupon",
    hint: "No first-order, time, or location lock.",
    icon: Ticket,
  },
  {
    value: "FIRST_ORDER",
    label: "First order",
    hint: "Only users placing their first order can apply it.",
    icon: Ticket,
  },
  {
    value: "DAY_TIME",
    label: "Day & time",
    hint: "Choose exact days and time window.",
    icon: Clock3,
  },
  {
    value: "LOCATION",
    label: "Location",
    hint: "Use IndraQ state, city, and pincode API.",
    icon: MapPin,
  },
];

const DEFAULT_DAY_OPTIONS = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];

function cleanName(value) {
  return String(value || "").trim().replace(/\s+/g, " ");
}

function token(value) {
  return cleanName(value).toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function uniqueList(values = []) {
  const seen = new Set();
  const out = [];
  values.forEach((value) => {
    const label = cleanName(value);
    const key = token(label);
    if (!label || seen.has(key)) return;
    seen.add(key);
    out.push(label);
  });
  return out;
}

function csvToList(value) {
  if (Array.isArray(value)) return uniqueList(value);
  return uniqueList(String(value || "").split(","));
}

function listToCsv(values = []) {
  return uniqueList(values).join(", ");
}

function pickApiArray(payload) {
  const value = payload?.data ?? payload?.items ?? payload?.results ?? payload?.states ?? payload?.cities ?? payload;
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") {
    return value.data || value.items || value.results || value.states || value.cities || value.postal || [];
  }
  return [];
}

function stateLabel(item) {
  if (typeof item === "string") return cleanName(item);
  return cleanName(item?.state || item?.stateName || item?.state_name || item?.name || item?.label || item?.value);
}

function districtLabel(item) {
  if (typeof item === "string") return cleanName(item);
  return cleanName(item?.district || item?.districtName || item?.district_name || item?.name || item?.label || item?.value);
}

function cityLabel(item) {
  if (typeof item === "string") return cleanName(item);
  return cleanName(
    item?.city ||
      item?.cityName ||
      item?.city_name ||
      item?.place ||
      item?.placeName ||
      item?.place_name ||
      item?.officeName ||
      item?.office_name ||
      item?.name ||
      item?.label ||
      item?.value
  );
}

function readApiLocationRecord(item = {}) {
  if (!item || typeof item !== "object" || Array.isArray(item)) return null;
  const state = stateLabel(item);
  const district = districtLabel(item);
  const city = cityLabel(item);
  const pincode = cleanName(item.pincode || item.pinCode || item.pin_code || item.postalCode || item.postal_code || item.code).replace(/\D+/g, "");
  if (!state && !district && !city && !pincode) return null;
  return { state, district, city, pincode };
}

function extractLocationRecords(payload) {
  const out = [];
  const seen = new Set();

  function add(record) {
    if (!record) return;
    const key = `${token(record.state)}:${token(record.district)}:${token(record.city)}:${record.pincode || ""}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(record);
  }

  function visit(value) {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (typeof value !== "object") return;
    add(readApiLocationRecord(value));
    Object.values(value).forEach((child) => {
      if (child && typeof child === "object") visit(child);
    });
  }

  pickApiArray(payload).forEach(visit);
  if (!out.length) visit(payload);
  return out;
}

function filterRecordsByState(records = [], state = "") {
  const stateKey = token(state);
  if (!stateKey) return [];
  return records.filter((record) => !record.state || token(record.state) === stateKey);
}

function filterRecordsByDistrict(records = [], district = "") {
  const districtKey = token(district);
  if (!districtKey) return [];
  return records.filter((record) => record.district && token(record.district) === districtKey);
}

async function fetchJson(url) {
  const response = await fetch(url);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data?.message || `Request failed: ${response.status}`);
  return data;
}

function getPostalLocation(payload) {
  const records = extractLocationRecords(payload);
  const record = records.find((item) => item.pincode || item.state || item.city || item.district) || {};
  return {
    state: cleanName(record.state),
    district: cleanName(record.district),
    city: cleanName(record.city),
    pincode: cleanName(record.pincode).replace(/\D+/g, ""),
  };
}

function SearchableDropdown({ label, value, onChange, options, placeholder, disabled, loading, allowCustom = false, emptyText = "No result found" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filteredOptions = useMemo(() => {
    const q = token(query);
    if (!q) return options.slice(0, 80);
    return options.filter((option) => token(option).includes(q)).slice(0, 80);
  }, [options, query]);

  const customValue = cleanName(query);
  const canUseCustom = allowCustom && customValue && !options.some((option) => token(option) === token(customValue));

  return (
    <div
      className="relative"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setOpen(false);
          setQuery("");
        }
      }}
    >
      <span className="text-[12px] font-black uppercase tracking-[0.18em] text-slate-500">{label}</span>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className="mt-2 flex h-[48px] w-full items-center justify-between gap-3 rounded-[16px] border border-rose-100 bg-white px-4 text-left text-[14px] font-semibold text-slate-700 outline-none transition hover:border-[#d86a78] disabled:bg-slate-50 disabled:text-slate-400"
      >
        <span className={value ? "truncate" : "truncate text-slate-400"}>{value || placeholder}</span>
        {loading ? <Loader2 size={16} className="shrink-0 animate-spin text-[#d86a78]" /> : <ChevronDown size={16} className="shrink-0 text-[#d86a78]" />}
      </button>

      {open && !disabled ? (
        <div className="absolute left-0 top-full z-40 mt-2 w-full overflow-hidden rounded-[18px] border border-rose-100 bg-white shadow-2xl shadow-rose-100/80">
          <div className="flex items-center gap-2 border-b border-rose-50 px-3 py-2">
            <Search size={15} className="text-slate-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-9 w-full bg-transparent text-[13px] font-semibold text-slate-700 outline-none placeholder:text-slate-400"
              placeholder="Search..."
            />
          </div>
          <div className="max-h-[240px] overflow-y-auto overscroll-contain p-1.5">
            {value ? (
              <button
                type="button"
                className="mb-1 flex w-full items-center rounded-[12px] px-3 py-2 text-left text-[13px] font-bold text-rose-600 hover:bg-rose-50"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange("");
                  setOpen(false);
                  setQuery("");
                }}
              >
                Clear selection
              </button>
            ) : null}
            {canUseCustom ? (
              <button
                type="button"
                className="mb-1 flex w-full items-center rounded-[12px] px-3 py-2 text-left text-[13px] font-black text-[#d86a78] hover:bg-rose-50"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(customValue);
                  setOpen(false);
                  setQuery("");
                }}
              >
                Use “{customValue}”
              </button>
            ) : null}
            {filteredOptions.length ? filteredOptions.map((option) => (
              <button
                key={option}
                type="button"
                className={`flex w-full items-center rounded-[12px] px-3 py-2 text-left text-[13px] font-bold transition ${token(option) === token(value) ? "bg-rose-50 text-[#d86a78]" : "text-slate-700 hover:bg-slate-50"}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(option);
                  setOpen(false);
                  setQuery("");
                }}
              >
                <span className="truncate">{option}</span>
              </button>
            )) : !canUseCustom ? (
              <div className="px-3 py-5 text-center text-[13px] font-bold text-slate-400">{emptyText}</div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Chip({ children, onRemove }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-rose-50 px-3 py-1.5 text-[12px] font-black text-[#d86a78]">
      <span className="truncate">{children}</span>
      {onRemove ? (
        <button type="button" onClick={onRemove} className="rounded-full p-0.5 hover:bg-rose-100">
          <X size={12} />
        </button>
      ) : null}
    </span>
  );
}

function PillButton({ children, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-xl border px-3 py-2 text-xs font-black transition ${active ? "border-[#d86a78] bg-[#fff1f3] text-[#cc4f60]" : "border-slate-200 bg-white text-slate-500 hover:border-rose-200 hover:text-[#d86a78]"}`}
    >
      {children}
    </button>
  );
}

function listFromForm(form, key) {
  return csvToList(form?.[key]);
}

export function buildCouponConditionPayload(form = {}) {
  const conditionType = String(form.conditionType || "NONE").toUpperCase();

  if (conditionType === "FIRST_ORDER") {
    return { firstOrderOnly: true };
  }

  if (conditionType === "DAY_TIME") {
    return {
      validDays: Array.isArray(form.validDays) ? form.validDays : [],
      validFromTime: form.validFromTime || null,
      validToTime: form.validToTime || null,
    };
  }

  if (conditionType === "LOCATION") {
    return {
      allowedCities: listFromForm(form, "allowedCities"),
      allowedStates: listFromForm(form, "allowedStates"),
      allowedPincodes: listFromForm(form, "allowedPincodes"),
    };
  }

  return {};
}

export function validateCouponCondition(form = {}) {
  const conditionType = String(form.conditionType || "NONE").toUpperCase();

  if (conditionType === "DAY_TIME") {
    const hasDays = Array.isArray(form.validDays) && form.validDays.length > 0;
    const hasTime = Boolean(form.validFromTime || form.validToTime);
    if (!hasDays && !hasTime) return "Select at least one valid day or time window, or choose Normal coupon.";
  }

  if (conditionType === "LOCATION") {
    const hasLocation = ["allowedCities", "allowedStates", "allowedPincodes"].some((key) => listFromForm(form, key).length > 0);
    if (!hasLocation) return "Add at least one allowed state, city, or pincode for the location coupon.";
  }

  return "";
}

export default function CouponConditionFields({ form, onChange, dayOptions = DEFAULT_DAY_OPTIONS }) {
  const conditionType = String(form.conditionType || "NONE").toUpperCase();
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [stateRecords, setStateRecords] = useState([]);
  const [draft, setDraft] = useState({ state: "", district: "", city: "", pincode: "" });
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [locationError, setLocationError] = useState("");

  const selectedStates = useMemo(() => listFromForm(form, "allowedStates"), [form.allowedStates]);
  const selectedCities = useMemo(() => listFromForm(form, "allowedCities"), [form.allowedCities]);
  const selectedPincodes = useMemo(() => listFromForm(form, "allowedPincodes"), [form.allowedPincodes]);

  useEffect(() => {
    let cancelled = false;
    setLoadingStates(true);
    fetchJson(`${PINCODE_API_BASE}/states?country=IN`)
      .then((payload) => {
        if (cancelled) return;
        setStates(uniqueList(pickApiArray(payload).map(stateLabel)).sort((a, b) => a.localeCompare(b)));
      })
      .catch(() => {
        if (!cancelled) setStates([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingStates(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const state = cleanName(draft.state);
    if (!state) {
      setDistricts([]);
      setCities([]);
      setStateRecords([]);
      return undefined;
    }

    let cancelled = false;
    const stateParam = encodeURIComponent(state);
    const firstWordParam = encodeURIComponent(state.split(/\s+/)[0] || state);

    setLoadingDistricts(true);
    setLoadingCities(true);
    setLocationError("");

    Promise.all([
      fetchJson(`${PINCODE_API_BASE}/districts?country=IN&state=${stateParam}`).catch(() => null),
      fetchJson(`${PINCODE_API_BASE}/cities?country=IN&state=${stateParam}`).catch(() => null),
      fetchJson(`${PINCODE_API_BASE}/postal-search?q=${stateParam}&country=IN`).catch(() => null),
      fetchJson(`${PINCODE_API_BASE}/postal-search?q=${firstWordParam}&country=IN`).catch(() => null),
    ])
      .then(([districtPayload, cityPayload, stateSearchPayload, prefixPayload]) => {
        if (cancelled) return;
        const records = [
          ...filterRecordsByState(extractLocationRecords(districtPayload), state),
          ...filterRecordsByState(extractLocationRecords(cityPayload), state),
          ...filterRecordsByState(extractLocationRecords(stateSearchPayload), state),
          ...filterRecordsByState(extractLocationRecords(prefixPayload), state),
        ];
        setStateRecords(records);
        setDistricts(uniqueList([...pickApiArray(districtPayload).map(districtLabel), ...records.map((item) => item.district)]).sort((a, b) => a.localeCompare(b)));
        setCities(uniqueList([...pickApiArray(cityPayload).map(cityLabel), ...records.map((item) => item.city)]).sort((a, b) => a.localeCompare(b)));
      })
      .catch(() => {
        if (!cancelled) {
          setDistricts([]);
          setCities([]);
          setStateRecords([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingDistricts(false);
          setLoadingCities(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [draft.state]);

  useEffect(() => {
    const state = cleanName(draft.state);
    const district = cleanName(draft.district);
    if (!state || !district) return undefined;

    const localCities = uniqueList(filterRecordsByDistrict(filterRecordsByState(stateRecords, state), district).map((item) => item.city)).sort((a, b) => a.localeCompare(b));
    if (localCities.length) {
      setCities(localCities);
      return undefined;
    }

    let cancelled = false;
    setLoadingCities(true);
    fetchJson(`${PINCODE_API_BASE}/postal-search?q=${encodeURIComponent(district)}&country=IN`)
      .then((payload) => {
        if (cancelled) return;
        const records = filterRecordsByDistrict(filterRecordsByState(extractLocationRecords(payload), state), district);
        setCities(uniqueList(records.map((item) => item.city)).sort((a, b) => a.localeCompare(b)));
      })
      .catch(() => {
        if (!cancelled) setCities([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingCities(false);
      });

    return () => {
      cancelled = true;
    };
  }, [draft.state, draft.district, stateRecords]);

  function setConditionType(nextType) {
    onChange("conditionType", nextType);
    onChange("firstOrderOnly", nextType === "FIRST_ORDER");
    onChange("validDays", []);
    onChange("validFromTime", "");
    onChange("validToTime", "");
    onChange("allowedCities", "");
    onChange("allowedStates", "");
    onChange("allowedPincodes", "");
    setDraft({ state: "", district: "", city: "", pincode: "" });
    setLocationError("");
  }

  function setDraftField(key, value) {
    setDraft((current) => {
      if (key === "state") return { state: value, district: "", city: "", pincode: current.pincode };
      if (key === "district") return { ...current, district: value, city: "" };
      return { ...current, [key]: value };
    });
  }

  function addValue(field, value) {
    const label = field === "allowedPincodes" ? cleanName(value).replace(/\D+/g, "") : cleanName(value);
    if (!label) return;
    onChange(field, listToCsv([...listFromForm(form, field), label]));
  }

  function removeValue(field, value) {
    onChange(field, listToCsv(listFromForm(form, field).filter((item) => token(item) !== token(value))));
  }

  async function lookupPincode() {
    const pincode = cleanName(draft.pincode).replace(/\D+/g, "");
    if (pincode.length !== 6) {
      setLocationError("Enter a valid 6 digit pincode.");
      return;
    }
    setPincodeLoading(true);
    setLocationError("");
    try {
      const payload = await fetchJson(`${PINCODE_API_BASE}/postal/${pincode}?country=IN`);
      const location = getPostalLocation(payload);
      setDraft((current) => ({
        state: location.state || current.state,
        district: location.district || current.district,
        city: location.city || current.city,
        pincode,
      }));
      if (location.state) setStates((current) => uniqueList([...current, location.state]).sort((a, b) => a.localeCompare(b)));
      if (location.district) setDistricts((current) => uniqueList([...current, location.district]).sort((a, b) => a.localeCompare(b)));
      if (location.city) setCities((current) => uniqueList([...current, location.city]).sort((a, b) => a.localeCompare(b)));
      addValue("allowedPincodes", pincode);
    } catch (err) {
      setLocationError(err?.message || "Invalid pincode.");
    } finally {
      setPincodeLoading(false);
    }
  }

  const activeCondition = CONDITION_TYPES.find((item) => item.value === conditionType) || CONDITION_TYPES[0];

  return (
    <div className="rounded-2xl border border-[#f2c6cc] bg-[#fffafa] p-4 md:col-span-3">
      <div>
        <div className="text-sm font-black text-slate-950">Special coupon type</div>
        <div className="text-xs text-slate-500">Keep one type active. First-order, day/time, and location coupons are separate.</div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-4">
        {CONDITION_TYPES.map((item) => {
          const Icon = item.icon;
          const active = conditionType === item.value;
          return (
            <button
              key={item.value}
              type="button"
              onClick={() => setConditionType(item.value)}
              className={`rounded-[18px] border p-4 text-left transition ${active ? "border-[#d86a78] bg-white shadow-sm" : "border-rose-100 bg-white/70 hover:border-[#f2c6cc]"}`}
            >
              <div className="flex items-center gap-2 text-sm font-black text-slate-950">
                <span className={`rounded-xl p-2 ${active ? "bg-[#fff1f3] text-[#d86a78]" : "bg-slate-50 text-slate-400"}`}><Icon size={15} /></span>
                {item.label}
              </div>
              <p className="mt-2 text-xs font-semibold leading-5 text-slate-500">{item.hint}</p>
            </button>
          );
        })}
      </div>

      {conditionType === "FIRST_ORDER" ? (
        <div className="mt-4 rounded-[18px] border border-emerald-100 bg-emerald-50 p-4 text-sm font-bold text-emerald-700">
          This coupon will apply only when the customer has no previous successful order.
        </div>
      ) : null}

      {conditionType === "DAY_TIME" ? (
        <div className="mt-4 rounded-[18px] border border-rose-100 bg-white p-4">
          <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Valid days</div>
          <div className="flex flex-wrap gap-2">
            {dayOptions.map((day) => {
              const selected = (form.validDays || []).includes(day.value);
              return <PillButton key={day.value} active={selected} onClick={() => onChange("validDays", day.value)}>{day.label}</PillButton>;
            })}
          </div>
          <div className="mt-1 text-[11px] text-slate-400">Leave days unselected to allow every day, but set a time window.</div>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <label className="block">
              <span className="text-[12px] font-black uppercase tracking-[0.18em] text-slate-500">Valid from time</span>
              <input className="mt-2 h-[48px] w-full rounded-[16px] border border-rose-100 px-4 text-sm font-semibold outline-none focus:border-[#d86a78]" type="time" value={form.validFromTime || ""} onChange={(e) => onChange("validFromTime", e.target.value)} />
            </label>
            <label className="block">
              <span className="text-[12px] font-black uppercase tracking-[0.18em] text-slate-500">Valid to time</span>
              <input className="mt-2 h-[48px] w-full rounded-[16px] border border-rose-100 px-4 text-sm font-semibold outline-none focus:border-[#d86a78]" type="time" value={form.validToTime || ""} onChange={(e) => onChange("validToTime", e.target.value)} />
            </label>
            <div className="flex items-end pb-3 text-xs font-semibold text-slate-500">Blank time side means open from start/end of day.</div>
          </div>
        </div>
      ) : null}

      {conditionType === "LOCATION" ? (
        <div className="mt-4 rounded-[18px] border border-rose-100 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-black text-slate-950">Location rules</div>
              <div className="text-xs text-slate-500">Data is loaded from the same IndraQ pincode/location API used in Food module visibility.</div>
            </div>
            {locationError ? <div className="rounded-full bg-rose-50 px-3 py-1 text-xs font-black text-rose-600">{locationError}</div> : null}
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-4">
            <SearchableDropdown
              label="State"
              value={draft.state}
              onChange={(value) => setDraftField("state", value)}
              options={states}
              placeholder="Select state"
              loading={loadingStates}
              allowCustom
            />
            <SearchableDropdown
              label="District"
              value={draft.district}
              onChange={(value) => setDraftField("district", value)}
              options={districts}
              placeholder={draft.state ? "Select district" : "Select state first"}
              disabled={!draft.state}
              loading={loadingDistricts}
              allowCustom
            />
            <SearchableDropdown
              label="City"
              value={draft.city}
              onChange={(value) => setDraftField("city", value)}
              options={cities}
              placeholder={draft.district ? "Select city" : "Select district first"}
              disabled={!draft.state || !draft.district}
              loading={loadingCities}
              allowCustom
            />
            <label className="block">
              <span className="text-[12px] font-black uppercase tracking-[0.18em] text-slate-500">Pincode</span>
              <div className="mt-2 flex h-[48px] overflow-hidden rounded-[16px] border border-rose-100 bg-white">
                <input
                  value={draft.pincode}
                  onChange={(e) => setDraftField("pincode", e.target.value.replace(/\D+/g, "").slice(0, 6))}
                  placeholder="Search pincode"
                  className="min-w-0 flex-1 px-4 text-sm font-semibold outline-none"
                />
                <button type="button" onClick={lookupPincode} disabled={pincodeLoading} className="shrink-0 bg-[#d86a78] px-3 text-xs font-black text-white disabled:opacity-60">
                  {pincodeLoading ? "..." : "Add"}
                </button>
              </div>
            </label>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" disabled={!draft.state} onClick={() => addValue("allowedStates", draft.state)} className="rounded-[12px] bg-[#d86a78] px-3 py-2 text-xs font-black text-white disabled:opacity-40">Add state</button>
            <button type="button" disabled={!draft.city} onClick={() => addValue("allowedCities", draft.city)} className="rounded-[12px] bg-[#d86a78] px-3 py-2 text-xs font-black text-white disabled:opacity-40">Add city</button>
            <button type="button" disabled={!draft.pincode || draft.pincode.length !== 6} onClick={() => addValue("allowedPincodes", draft.pincode)} className="rounded-[12px] bg-[#d86a78] px-3 py-2 text-xs font-black text-white disabled:opacity-40">Add pincode only</button>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <div className="rounded-[16px] border border-rose-50 bg-[#fffafa] p-3">
              <div className="mb-2 text-xs font-black uppercase tracking-wide text-slate-500">Allowed states</div>
              <div className="flex flex-wrap gap-2">
                {selectedStates.length ? selectedStates.map((item) => <Chip key={item} onRemove={() => removeValue("allowedStates", item)}>{item}</Chip>) : <span className="text-xs font-semibold text-slate-400">No state selected</span>}
              </div>
            </div>
            <div className="rounded-[16px] border border-rose-50 bg-[#fffafa] p-3">
              <div className="mb-2 text-xs font-black uppercase tracking-wide text-slate-500">Allowed cities</div>
              <div className="flex flex-wrap gap-2">
                {selectedCities.length ? selectedCities.map((item) => <Chip key={item} onRemove={() => removeValue("allowedCities", item)}>{item}</Chip>) : <span className="text-xs font-semibold text-slate-400">No city selected</span>}
              </div>
            </div>
            <div className="rounded-[16px] border border-rose-50 bg-[#fffafa] p-3">
              <div className="mb-2 text-xs font-black uppercase tracking-wide text-slate-500">Allowed pincodes</div>
              <div className="flex flex-wrap gap-2">
                {selectedPincodes.length ? selectedPincodes.map((item) => <Chip key={item} onRemove={() => removeValue("allowedPincodes", item)}>{item}</Chip>) : <span className="text-xs font-semibold text-slate-400">No pincode selected</span>}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {conditionType === "NONE" ? (
        <div className="mt-4 rounded-[18px] border border-slate-100 bg-white p-4 text-sm font-semibold text-slate-500">
          {activeCondition.hint}
        </div>
      ) : null}
    </div>
  );
}
